import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
	static targets = ['min', 'max', 'minOutput', 'maxOutput', 'fill', 'track', 'minHandle', 'maxHandle', 'currencySelect'];
	static values = {
		suffix: { type: String, default: '' },
	};

	connect() {
		this.activeHandle = null;
		this.numberFormatter = new Intl.NumberFormat(document.documentElement.lang || undefined);
		this.onPointerMove = this.onPointerMove.bind(this);
		this.onPointerUp = this.onPointerUp.bind(this);
		this.onExternalSync = this.onExternalSync.bind(this);
		this.onCurrencyChange = this.onCurrencyChange.bind(this);
		this._handleFormReset = this._handleFormReset.bind(this);
		this.form = this.element.closest('form');
		this.element.addEventListener('range-pair:sync', this.onExternalSync);
		window.addEventListener('range-pair:currency-changed', this.onCurrencyChange);
		this.form?.addEventListener('reset', this._handleFormReset);
		this.sync();
	}

	disconnect() {
		window.removeEventListener('pointermove', this.onPointerMove);
		window.removeEventListener('pointerup', this.onPointerUp);
		this.element.removeEventListener('range-pair:sync', this.onExternalSync);
		window.removeEventListener('range-pair:currency-changed', this.onCurrencyChange);
		this.form?.removeEventListener('reset', this._handleFormReset);
	}

	updateMin() {
		if (Number(this.minTarget.value) > Number(this.maxTarget.value)) {
			this.maxTarget.value = this.minTarget.value;
		}
		this.sync();
	}

	updateMax() {
		if (Number(this.maxTarget.value) < Number(this.minTarget.value)) {
			this.minTarget.value = this.maxTarget.value;
		}
		this.sync();
	}

	startMinDrag(e) {
		e.preventDefault();
		this.startDrag('min');
	}

	startMaxDrag(e) {
		e.preventDefault();
		this.startDrag('max');
	}

	startDrag(handle) {
		this.activeHandle = handle;
		window.addEventListener('pointermove', this.onPointerMove);
		window.addEventListener('pointerup', this.onPointerUp);
	}

	onPointerMove(e) {
		if (!this.activeHandle || !this.hasTrackTarget) return;

		const rect = this.trackTarget.getBoundingClientRect();
		const ratio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
		const min = Number(this.minTarget.min || 0);
		const max = Number(this.maxTarget.max || 100);
		const step = Number(this.minTarget.step || this.maxTarget.step || 1);
		const rawValue = min + ratio * (max - min);
		const steppedValue = Math.round(rawValue / step) * step;

		if (this.activeHandle === 'min') {
			this.minTarget.value = String(Math.min(steppedValue, Number(this.maxTarget.value)));
			this.updateMin();
			return;
		}

		this.maxTarget.value = String(Math.max(steppedValue, Number(this.minTarget.value)));
		this.updateMax();
	}

	onPointerUp() {
		this.activeHandle = null;
		window.removeEventListener('pointermove', this.onPointerMove);
		window.removeEventListener('pointerup', this.onPointerUp);
	}

	onExternalSync() {
		this.sync();
	}

	/**
	 * Fired after the native form reset has already run (inputs reverted to defaultValue).
	 * Explicitly resets to the slider's full range (min/max attrs) so the visual always
	 * reflects a fully-cleared filter, regardless of what the server rendered as the initial value.
	 */
	_handleFormReset() {
		this.minTarget.value = this.minTarget.getAttribute('min') ?? this.minTarget.min;
        this.maxTarget.value = this.maxTarget.getAttribute('max') ?? this.maxTarget.max;
		this.sync();
	}

	/**
	 * Reads a numeric value from an input, falling back to the given attribute
	 * bound ("min"/"max") when the value is empty or not a number (e.g. after
	 * a facet-remove blanked the input), and clamps it to the input's bounds.
	 */
	_readValue(input, fallbackAttr) {
		const bound = Number(input.getAttribute(fallbackAttr));
		const v = Number.parseFloat(input.value);
		if (!Number.isFinite(v)) return bound;
		const min = Number(input.getAttribute('min'));
		const max = Number(input.getAttribute('max'));
		return Math.min(Math.max(v, Number.isFinite(min) ? min : v), Number.isFinite(max) ? max : v);
	}

	sync() {
		// Self-heal blank/invalid values (e.g. after facet removal) back to full range.
		const minValue = this._readValue(this.minTarget, 'min');
		const maxValue = this._readValue(this.maxTarget, 'max');
		this.minTarget.value = String(minValue);
		this.maxTarget.value = String(maxValue);

		this.minOutputTarget.textContent = this._formatValue(minValue);
		this.maxOutputTarget.textContent = this._formatValue(maxValue);

		const min = Number(this.minTarget.min || 0);
		const max = Number(this.maxTarget.max || 100);
		const range = max - min || 1;
		const start = ((minValue - min) / range) * 100;
		const end = ((maxValue - min) / range) * 100;

		if (this.hasFillTarget) {
			this.fillTarget.style.left = `${start}%`;
			this.fillTarget.style.width = `${Math.max(end - start, 0)}%`;
		}

		if (this.hasMinHandleTarget) {
			this.minHandleTarget.style.left = `${start}%`;
		}

		if (this.hasMaxHandleTarget) {
			this.maxHandleTarget.style.left = `${end}%`;
		}

		this.minTarget.dispatchEvent(new Event('input', { bubbles: true }));
		this.maxTarget.dispatchEvent(new Event('input', { bubbles: true }));
		this.minTarget.dispatchEvent(new Event('change', { bubbles: true }));
		this.maxTarget.dispatchEvent(new Event('change', { bubbles: true }));
	}

	onCurrencyChange(e) {
		if (e.detail.targetId && e.detail.targetId !== this.element.id) return;

		const { min, max, step, symbol } = e.detail;

		this.minTarget.min = String(min);
		this.minTarget.max = String(max);
		this.minTarget.step = String(step);
		this.minTarget.value = String(min);

		this.maxTarget.min = String(min);
		this.maxTarget.max = String(max);
		this.maxTarget.step = String(step);
		this.maxTarget.value = String(max);

		this.suffixValue = symbol;
		this.sync();
	}

	currencyChanged() {
		if (!this.hasCurrencySelectTarget) return;

		// Support both <select> (.selectedOptions) and <input type="radio"> (multiple targets)
		let selected;
		if (typeof this.currencySelectTarget.selectedOptions !== 'undefined') {
			selected = this.currencySelectTarget.selectedOptions[0];
		} else {
			selected = this.currencySelectTargets.find(el => el.checked);
		}

		if (!selected) return;

		const min = selected.dataset.min !== undefined ? Number(selected.dataset.min) : Number(this.minTarget.min);
		const max = selected.dataset.max !== undefined ? Number(selected.dataset.max) : Number(this.maxTarget.max);
		const step = selected.dataset.step !== undefined ? Number(selected.dataset.step) : Number(this.minTarget.step);
		const symbol = selected.dataset.symbol !== undefined ? selected.dataset.symbol : this.suffixValue;

		this.minTarget.min = String(min);
		this.minTarget.max = String(max);
		this.minTarget.step = String(step);
		this.minTarget.value = String(min);

		this.maxTarget.min = String(min);
		this.maxTarget.max = String(max);
		this.maxTarget.step = String(step);
		this.maxTarget.value = String(max);

		this.suffixValue = symbol;
		this.sync();
	}

	_formatValue(value) {
		const formattedValue = this.numberFormatter.format(value);
		return this.suffixValue ? `${formattedValue} ${this.suffixValue}` : formattedValue;
	}
}
